import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, CircularProgress } from '@mui/material';
import { MRT_Row, MRT_RowSelectionState, MaterialReactTable } from 'material-react-table';
import { styled } from '@mui/material/styles';
import { ChartResponseDto, InvoiceAnalyticsDto } from 'src/generated/services/TFinancialApi';
import { useColumnVisibility } from 'src/presentations/components/molecules/table/columnSettings';

import { TableTab, useAnalyticsTable } from './useTable';
import MassActions from './MassActions';

// Common styles for the last cell in both header and body rows
const lastCellStyles = {
  boxShadow: '-4px 0px 12px 0px rgba(89, 114, 130, 0.08)',
  borderLeft: '1px solid #E5E7EB',
};

export const TableWrapper = styled(Paper)({
  boxShadow: 'none',
});

const getRowId = (row: any) => row?.invoiceId ?? row?.id;

export const TableMain = styled(Box)(({ theme }) => ({
  '& .MuiTableCell-head': {
    fontSize: '14px',
    fontWeight: 'bold',
    backgroundColor: '#F5F6FA',
    padding: '8px 16px',
    verticalAlign: 'middle',
    [theme.breakpoints.down(1520)]: {
      fontSize: '12px',
      padding: '6px 12px',
    },
  },
  '& .MuiTableCell-body ': {
    padding: '16px',
    fontSize: '14px',
    borderRight: '1px solid #DFDFDF',
    borderBottom: '1px solid #DFDFDF',
    [theme.breakpoints.down(1520)]: {
      fontSize: '12px',
      padding: '14px 12px',
    },
    '&:last-child': {
      borderRight: 0,
      borderBottom: 0,
    },
  },
  '& .Mui-selected td': {
    backgroundColor: '#F5F6FA',
  },
  '& .MuiTableRow-root:hover td': {
    backgroundColor: '#F5F6FA',
  },
}));

interface TableDetailProps {
  tableDetail: {
    columns: any[];
    data?: any[];
    isLoading?: boolean;
    page?: any;
    total?: number;
    isTabVisited?: boolean;
  };
  chartData: ChartResponseDto;
  page: any;
  tableTab: TableTab;
  refetchData: () => void;
  onSorting: (method: string, fieldName: string) => void;
}

export const TableDetail = ({ tableDetail, chartData, page, tableTab, refetchData, onSorting }: TableDetailProps) => {
  const { columns: columnConfigs } = useColumnVisibility();
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [sorting, setSorting] = useState([{ id: 'INVOICE_DATE', desc: false }]);
  const isInvoiceTab = tableTab === TableTab.INVOICES;
  const isShipmentTab = tableTab === TableTab.SHIPMENTS;
  const isChargeTab = tableTab === TableTab.CHARGES;

  // Filter and order columns based on visibility settings
  const visibleColumns = useMemo(() => {
    const defaultColumnsMap = new Map(tableDetail.columns.map((col: any) => [col.accessorKey as string, col]));

    return columnConfigs.filter((config) => config.isVisible && defaultColumnsMap.has(config.id)).map((config) => defaultColumnsMap.get(config.id)!);
  }, [tableDetail.columns, columnConfigs]);

  const invoiceFilterConfig = useMemo(
    () => ({
      invoiceNumber: { columnName: 'INVOICE_NUMBER' },
      fapRef: { columnName: 'FAP_REF' },
      billedEntity: { columnName: 'BILLED_ENTITY_NAME' },
      invoiceStatus: { columnName: 'INVOICE_STATUS' },
      reason: { columnName: 'INVOICE_REASON' },
      serviceProvider: { columnName: 'SERVICE_PROVIDER_NAME' },
      modality: { columnName: 'MODALITY' },
      accountNumber: { columnName: 'ACCOUNT_NUMBER' },
      shipmentNumber: { columnName: 'SHIPMENT_NUMBER' },
    }),
    []
  );

  const invoiceSortOnlyColumns = useMemo(() => new Set(['date', 'totalAmount', 'totalAmountInReferenceCurrency', 'resolutionDate', 'invoiceChargesCount']), []);

  const invoiceSortableColumns = useMemo(() => {
    const filterColumns = Object.keys(invoiceFilterConfig);
    return new Set([...filterColumns, ...invoiceSortOnlyColumns]);
  }, [invoiceFilterConfig, invoiceSortOnlyColumns]);

  const handleInvoiceSort = useCallback(
    (columnId: string, direction: 'asc' | 'desc') => {
      const method = direction === 'desc' ? 'DESC' : 'ASC';
      onSorting(method, columnId);
    },
    [onSorting]
  );

  const shipmentFilterConfig = useMemo(
    () => ({
      shipmentNumber: { columnName: 'SHIPMENT_NUMBER' },
      mbl: { columnName: 'MBL' },
      serviceType: { columnName: 'SERVICE_TYPE' },
      invoiceNumber: { columnName: 'INVOICE_NUMBER' },
      serviceProvider: { columnName: 'SERVICE_PROVIDER' },
      modality: { columnName: 'MODALITY' },
      billedEntity: { columnName: 'BILLED_ENTITY' },
      pol: { columnName: 'POL' },
      pod: { columnName: 'POD' },
      shipper: { columnName: 'SHIPPER_NAME' },
      shipperCountry: { columnName: 'SHIPPER_COUNTRY' },
      consignee: { columnName: 'CONSIGNEE_NAME' },
      consigneeCountry: { columnName: 'CONSIGNEE_COUNTRY' },
      goodsName: { columnName: 'GOOD_NAME' },
      vehicleId: { columnName: 'VEHICLE_ID' },
      shipmentCurrentDeviationWorkflow: { columnName: 'SHIPMENT_CURRENT_DEVIATION_WORKFLOW' },
      shipmentStatus: { columnName: 'SHIPMENT_STATUS' },
      shipmentStatusReason: { columnName: 'SHIPMENT_STATUS_REASON' },
    }),
    []
  );

  const chargeFilterConfig = useMemo(
    () => ({
      chargeDomain: { columnName: 'CHARGE_DOMAIN' },
      chargeName: { columnName: 'CHARGE_NAME' },
      legType: { columnName: 'LEG_TYPE' },
      chargeStatus: { columnName: 'CHARGE_STATUS' },
      chargeStatusReason: { columnName: 'CHARGE_STATUS_REASON' },
      serviceProvider: { columnName: 'SERVICE_PROVIDER_NAME' },
      modality: { columnName: 'MODALITY' },
      billedEntity: { columnName: 'BILLED_ENTITY' },
      quantityUnit: { columnName: 'QUANTITY_UNIT' },
      shipmentNumber: { columnName: 'SHIPMENT_NUMBER' },
      invoiceNumber: { columnName: 'INVOICE_NUMBER' },
      pol: { columnName: 'POL' },
      pod: { columnName: 'POD' },
    }),
    []
  );

  const shipmentSortOnlyColumns = useMemo(
    () =>
      new Set([
        'date',
        'leadTime',
        'invoiceAmountAfterTax',
        'totalAmount',
        'totalChargeAmountLeg1',
        'totalChargeAmountLeg2',
        'totalChargeAmountLeg3',
        'grossWeight',
        'chargeableWeight',
        'volume',
        'co2Emission',
      ]),
    []
  );

  const shipmentSortableColumns = useMemo(() => {
    const filterColumns = Object.keys(shipmentFilterConfig);
    return new Set([...filterColumns, ...shipmentSortOnlyColumns]);
  }, [shipmentFilterConfig, shipmentSortOnlyColumns]);

  const chargeSortOnlyColumns = useMemo(
    () =>
      new Set([
        'deviationAmountInInvoiceCurrency',
        'chargeAmount',
        'chargeAmountInInvoiceCurrency',
        'chargeAmountInReferenceCurrency',
        'chargeQuantity',
        'chargeUnitPrice',
        'exchangeRate',
        'date',
      ]),
    []
  );

  // Columns come pre-enhanced from dedicated table components (InvoiceTable, ShipmentTable, ChargeTable)
  const enhancedColumns = useMemo(() => tableDetail.columns, [tableDetail.columns]);

  const columnOrder = useMemo(() => {
    const dataColumns = enhancedColumns.map((col: any) => col.accessorKey as string);
    return isInvoiceTab ? ['mrt-row-select', ...dataColumns] : dataColumns;
  }, [enhancedColumns, isInvoiceTab]);

  // Clear selection when tab changes
  useEffect(() => {
    setRowSelection({});
  }, [tableTab]);

  // Apply sorting only when user manually changes sorting (not on initial load)
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    // Skip the initial load to prevent automatic API calls
    if (isInitialLoad) {
      setIsInitialLoad(false);
      return;
    }

    if (isInvoiceTab || isShipmentTab || isChargeTab) {
      return;
    }

    const runSorting = (sortingConfig) => {
      if (sortingConfig.length > 0) {
        const config = sortingConfig[0];
        const method = config?.desc ? 'DESC' : 'ASC';
        onSorting(method, config?.id);
      } else {
        onSorting('ASC', 'INVOICE_DATE');
      }
    };
    runSorting(sorting);
  }, [sorting, onSorting, isInitialLoad, isInvoiceTab, isShipmentTab, isChargeTab]);

  // Compute selected items
  const selectedItems = useMemo(() => {
    const selectedIds = Object.keys(rowSelection).filter((key) => rowSelection[key]);
    if (!tableDetail.data || selectedIds.length === 0) return [];
    return tableDetail.data.filter((item: any) => selectedIds.includes(item?.invoiceId ?? item?.id));
  }, [rowSelection, tableDetail.data]);

  // Table instance
  const table = useAnalyticsTable({
    ...tableDetail,
    columns: enhancedColumns,
    data: tableDetail.data ?? [],
    getRowId,
    enableSorting: !isInvoiceTab && !isShipmentTab && !isChargeTab,
    manualSorting: !isInvoiceTab && !isShipmentTab && !isChargeTab,
    state: {
      rowSelection,
      sorting: isInvoiceTab || isShipmentTab || isChargeTab ? [] : sorting,
      columnOrder,
      isLoading: tableDetail.isLoading, // Show loading state properly
    },
    onSortingChange: isInvoiceTab || isShipmentTab || isChargeTab ? undefined : setSorting,
    enableRowSelection: tableTab === TableTab.INVOICES ? (row: MRT_Row<InvoiceAnalyticsDto>) => row?.original?.invoiceStatus === 'APPROVED' : false,
    onRowSelectionChange: setRowSelection,
    renderEmptyRowsFallback: () => <p className='text-center text-16 font-medium uppercase text-neutral-40 py-30'>NO DATA AVAILABLE</p>,
    enableColumnPinning: true,
    enableStickyHeader: true,
    initialState: { columnPinning: { right: ['actions'] } },
    muiTableHeadProps: {
      sx: {
        '& th:last-child': lastCellStyles,
      },
    },
    muiTableBodyProps: {
      sx: {
        '& td:last-child': lastCellStyles,
      },
    },
    muiTablePaperProps: {
      sx: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 'none',
        backgroundColor: 'transparent',
      },
    },
    muiTableContainerProps: {
      sx: {
        flex: '1 1 0',
        minHeight: 0,
        overflowX: 'scroll',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          height: '12px',
          width: '12px',
          backgroundColor: '#f1f1f1',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#BDBDBD',
          borderRadius: '6px',
          border: '3px solid #f1f1f1',
          backgroundClip: 'content-box',
          '&:hover': {
            backgroundColor: '#999999',
          },
        },
      },
    },
  });

  return (
    <>
      <div className='w-full h-full bg-blue_grey-5 overflow-hidden flex flex-col'>
        <TableMain sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Show custom loading indicator for newly visited tabs */}
          {tableDetail.isLoading && (!tableDetail.data || tableDetail.data.length === 0) ? (
            <div className='flex items-center justify-center py-20'>
              <CircularProgress size={40} />
            </div>
          ) : (
            <MaterialReactTable table={table} />
          )}
        </TableMain>
      </div>

      {isInvoiceTab && (
        <MassActions
          selectedItems={selectedItems}
          handleClearSelection={() => setRowSelection({})}
          chartData={chartData}
          page={page}
          tableTab={tableTab}
          refetchData={refetchData}
        />
      )}
    </>
  );
};
